import * as React from 'react';
import { ChevronLeft } from 'lucide-react';
import { z } from 'zod';
import { Button } from './Button';
import { Form, FormField, type ZodForm } from './Form';
import { Icon } from './Icon';
import { Input } from './Input';
import { Center, Stack } from './Layout';
import { Text, TextLink } from './Text';

export const passwordResetSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
});

export type PasswordResetValues = z.infer<typeof passwordResetSchema>;
export type PasswordResetForm = ZodForm<typeof passwordResetSchema>;

export interface PasswordResetFormProps {
  /** From `useZodForm(passwordResetSchema, …)`. A failed send goes on it through `applyServerErrors`. */
  form: PasswordResetForm;
  /** A send is in flight — the first one, or a "send it again". */
  submitting: boolean;
  /** Why the last "send it again" failed, on the confirmation. */
  error?: string | null;
  onSubmit: (values: PasswordResetValues) => void;
  onBack: () => void;
  /**
   * Where the link went. Set, the form is replaced by the confirmation:
   * check your inbox, send it again, use a different email.
   */
  sentTo?: string | null;
  /** How many times it has gone to `sentTo`; past one the copy says so. */
  sentCount?: number;
  onResend: () => void;
  onUseDifferentEmail: () => void;
  /**
   * The bot check's container (Cloudflare Turnstile), drawn above the
   * submit button, and on the confirmation so "send it again" is checked too.
   */
  botCheck?: React.ReactNode;
}

/**
 * The forgot-password screen, in both of its states: the email form, then
 * the "check your inbox" confirmation once the link has gone. The backend
 * answers the same whether or not the address has an account, so the
 * confirmation says "if", not "we found you".
 *
 *   const form = useZodForm(passwordResetSchema, { defaultValues: { email } });
 *   <PasswordResetForm form={form} submitting={sending}
 *     onSubmit={send} onBack={backToSignIn}
 *     sentTo={sentTo} sentCount={count} error={resendError}
 *     onResend={resend} onUseDifferentEmail={reset} />
 */
export function PasswordResetForm({
  form,
  submitting,
  error,
  onSubmit,
  onBack,
  sentTo,
  sentCount = 0,
  onResend,
  onUseDifferentEmail,
  botCheck,
}: PasswordResetFormProps) {
  if (sentTo) {
    return (
      <Stack gap={8}>
        <Stack gap={2}>
          <Text variant="h2" tone="default">
            Check your inbox
          </Text>
          <Text variant="body-sm" tone="muted">
            If {sentTo} has a Relay Tow account, a link to set a new password is
            on its way. It expires after a day. If you signed up with Google,
            the email says so instead.
          </Text>
        </Stack>

        <Stack gap={3}>
          <Button size="xl" fullWidth onClick={onBack}>
            Back to sign in
          </Button>
          {botCheck}
          {error ? (
            <Text
              variant="caption"
              tone="destructive"
              align="center"
              role="alert"
            >
              {error}
            </Text>
          ) : sentCount > 1 ? (
            <Text variant="caption" tone="muted" align="center">
              Sent again. Give it a minute and check spam.
            </Text>
          ) : (
            <Text variant="caption" tone="muted" align="center">
              Nothing after a few minutes? Check spam, then{' '}
              <TextLink as="button" disabled={submitting} onClick={onResend}>
                {submitting ? 'sending…' : 'send it again'}
              </TextLink>
              .
            </Text>
          )}
          <Text variant="caption" tone="muted" align="center">
            Wrong address?{' '}
            <TextLink as="button" onClick={onUseDifferentEmail}>
              Use a different email
            </TextLink>
          </Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={8}>
      <Stack gap={2}>
        <Text variant="h2" tone="default">
          Reset your password
        </Text>
        <Text variant="body-sm" tone="muted">
          Enter the email you log in with. We will send you a link to set a new
          password.
        </Text>
      </Stack>

      <Form form={form} gap={4} onSubmit={onSubmit} errorPlacement="bottom">
        <FormField form={form} name="email" label="Email">
          {(field) => (
            <Input
              {...field}
              type="email"
              placeholder="jane@acme.com"
              disabled={submitting}
              autoFocus
              size="lg"
            />
          )}
        </FormField>

        {botCheck}
        <Button size="xl" type="submit" disabled={submitting} fullWidth>
          {submitting ? 'Sending…' : 'Send reset link'}
        </Button>
      </Form>

      <Center>
        <Button size="inline" variant="subtle" onClick={onBack}>
          <Icon icon={ChevronLeft} />
          Back to sign in
        </Button>
      </Center>
    </Stack>
  );
}
